import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MessageCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { useSite } from "@/context/SiteContext";

const field = "w-full rounded-xl border border-navy/15 bg-white px-4 py-3 font-sans text-sm text-charcoal focus:outline-none focus:border-navy focus:ring-2 focus:ring-champagne/40 transition-colors";
const labelCls = "block text-xs font-sans tracking-wide uppercase text-navy/60 mb-2";

const empty = {
  first_name: "", last_name: "", email: "", phone: "",
  preferred_contact: "", treatment_interest: "",
};

const BookingModal = () => {
  const { t, lang, bookingOpen, setBookingOpen, prefill, openWhatsApp, API, track } = useSite();
  const [type, setType] = useState("in_person");
  const [form, setForm] = useState(empty);
  const [consent, setConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const b = t.booking;

  useEffect(() => {
    if (bookingOpen) {
      setForm({ ...empty, ...(prefill || {}) });
      setConsent(false); setSmsConsent(false); setDone(false); setType("in_person"); setError("");
    }
  }, [bookingOpen, prefill]);

  useEffect(() => {
    document.body.style.overflow = bookingOpen ? "hidden" : "";
    document.body.classList.toggle("chat-widget-hidden", bookingOpen);
    return () => { document.body.style.overflow = ""; document.body.classList.remove("chat-widget-hidden"); };
  }, [bookingOpen]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const es = lang === "es";
  const method = (form.preferred_contact || "").toLowerCase();
  const wantsEmail = method === "email" || method === "correo";
  const wantsPhone = method === "phone" || method === "teléfono" || method === "telefono" || method === "whatsapp";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.preferred_contact || !consent) {
      setError(b.required);
      return;
    }
    if (wantsEmail && !form.email) { setError(b.required); return; }
    if (wantsPhone && !form.phone) { setError(b.required); return; }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        consultation_type: type === "in_person" ? "In-Person" : "Virtual",
        first_name: form.first_name, last_name: form.last_name,
        email: wantsEmail && form.email ? form.email : null,
        phone: wantsPhone ? form.phone : "",
        preferred_contact: form.preferred_contact,
        treatment_interest: form.treatment_interest || (es ? "No estoy seguro / Necesito orientación" : "Not Sure / I Need Guidance"),
        consent, sms_consent: smsConsent, sms_marketing_consent: false, language: lang,
      });
      track("consultation_request_completed", { type, method: form.preferred_contact });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const apiMsg = Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : detail;
      setError(apiMsg || (es
        ? "Algo salió mal. Inténtalo de nuevo o escríbenos por WhatsApp."
        : "Something went wrong. Please try again or reach us on WhatsApp."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {bookingOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-navy/60 backdrop-blur-sm flex items-start sm:items-center justify-center overflow-y-auto p-4 sm:p-6"
          onClick={() => setBookingOpen(false)}
          data-testid="booking-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl bg-offwhite rounded-3xl shadow-2xl my-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setBookingOpen(false)}
              className="absolute top-5 right-5 z-10 grid place-items-center w-10 h-10 rounded-full bg-white border border-navy/10 text-navy hover:bg-navy hover:text-white transition-colors"
              data-testid="booking-close">
              <X size={18} />
            </button>

            {done ? (
              <div className="p-10 sm:p-14 text-center" data-testid="booking-success">
                <div className="w-16 h-16 rounded-full bg-champagne/30 text-navy grid place-items-center mx-auto mb-6">
                  <Check size={30} />
                </div>
                <h3 className="font-serif text-3xl sm:text-4xl text-navy">{b.successTitle}</h3>
                <p className="font-sans text-charcoal/65 mt-4 max-w-md mx-auto">{b.successBody}</p>
                <button onClick={() => openWhatsApp()}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-6 py-3.5 font-medium hover:brightness-110 transition-[filter]"
                  data-testid="booking-success-wa">
                  <MessageCircle size={18} /> {b.waPrompt}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-7 sm:p-10">
                <p className="text-xs tracking-[0.25em] uppercase text-navy/50 font-sans">{b.chapter}</p>
                <h3 className="font-serif text-3xl sm:text-4xl text-navy mt-2">{b.title}</h3>
                <p className="font-sans text-charcoal/60 text-sm mt-2">{b.sub}</p>

                <div className="mt-6">
                  <span className={labelCls}>{b.typeLabel}</span>
                  <div className="grid grid-cols-2 gap-3">
                    {[["in_person", b.inPerson], ["virtual", b.virtual]].map(([v, lbl]) => (
                      <button type="button" key={v} onClick={() => setType(v)}
                        className={`rounded-xl border px-4 py-3.5 text-sm font-sans transition-colors ${type === v ? "bg-navy text-white border-navy" : "bg-white text-navy border-navy/15 hover:border-navy/40"}`}
                        data-testid={`booking-type-${v}`}>
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-5">
                  <div><label className={labelCls}>{b.firstName} *</label><input className={field} value={form.first_name} onChange={set("first_name")} data-testid="booking-first" /></div>
                  <div><label className={labelCls}>{b.lastName} *</label><input className={field} value={form.last_name} onChange={set("last_name")} data-testid="booking-last" /></div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>{b.contactMethod} *</label>
                    <select className={field} value={form.preferred_contact} onChange={set("preferred_contact")} data-testid="booking-contact">
                      <option value="">{b.selectPlaceholder}</option>
                      {b.contactOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  {wantsEmail && (
                    <div className="sm:col-span-2"><label className={labelCls}>{b.email} *</label><input type="email" className={field} value={form.email} onChange={set("email")} data-testid="booking-email" /></div>
                  )}
                  {wantsPhone && (
                    <div className="sm:col-span-2"><label className={labelCls}>{b.phone} *</label><input className={field} value={form.phone} onChange={set("phone")} data-testid="booking-phone" /></div>
                  )}
                  <div className="sm:col-span-2">
                    <label className={labelCls}>{b.treatment} ({es ? "Opcional" : "Optional"})</label>
                    <select className={field} value={form.treatment_interest} onChange={set("treatment_interest")} data-testid="booking-treatment">
                      <option value="">{b.selectPlaceholder}</option>
                      {b.treatmentOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-navy/10 bg-white p-4" data-testid="booking-sms-consent-label">
                      <input type="checkbox" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-navy shrink-0" data-testid="booking-sms-consent" />
                      <span className="font-sans text-[11px] leading-relaxed text-charcoal/70">
                        {es
                          ? "Acepto recibir mensajes SMS de REVIVE Regenerative Medical Clinic relacionados con confirmaciones de citas, recordatorios, programación y reprogramación, seguimientos de consulta, actualizaciones de servicios y mensajes promocionales ocasionales. La frecuencia de los mensajes varía. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es una condición de compra. Responda STOP para cancelar o HELP para obtener ayuda."
                          : "I agree to receive SMS messages from REVIVE Regenerative Medical Clinic regarding appointment confirmations, reminders, scheduling and rescheduling, consultation follow-ups, service updates, and occasional promotional messages. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase. Reply STOP to opt out or HELP for assistance."}
                      </span>
                    </label>
                    <p className="font-sans text-[11px] leading-relaxed text-charcoal/55 mt-2 px-1" data-testid="booking-acknowledgement">
                      By submitting this form, you acknowledge our{" "}
                      <a href="https://revivestem.com.mx/privacy" target="_blank" rel="noreferrer" className="underline hover:text-navy">Privacy Policy</a>{" "}
                      and{" "}
                      <a href="https://revivestem.com.mx/terms" target="_blank" rel="noreferrer" className="underline hover:text-navy">Terms of Use</a>.
                    </p>
                  </div>
                </div>

                {error && (
                  <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-sans flex items-center justify-between gap-3" data-testid="booking-error">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError("")} className="text-red-400 hover:text-red-600 shrink-0" aria-label="Dismiss"><X size={15} /></button>
                  </div>
                )}

                <label className="flex items-start gap-3 mt-5 cursor-pointer" data-testid="booking-consent-label">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-navy" data-testid="booking-consent" />
                  <span className="font-sans text-xs text-charcoal/70 leading-relaxed">{b.consent}</span>
                </label>

                <button type="submit" disabled={loading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white px-8 py-4 font-medium hover:bg-royal transition-colors disabled:opacity-60"
                  data-testid="booking-submit">
                  {loading ? b.submitting : b.submit}
                  {!loading && <ArrowRight size={17} />}
                </button>

                <button type="button" onClick={() => openWhatsApp()}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-navy/70 hover:text-navy transition-colors py-2"
                  data-testid="booking-wa">
                  <MessageCircle size={16} /> {b.waPrompt}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
